/**
 * 通用请求头接口定义
 */
export interface CommonHeaders {
  clientType?: string;
  deviceId?: string;
  lang?: string;
  versionCode?: string;
  'Content-Type': string;
  [key: string]: string | undefined; // 添加索引签名解决类型错误
}

// 服务端全局请求头存储
let globalServerHeaders: CommonHeaders = {
  'Content-Type': 'application/json',
};

/**
 * 获取默认请求头
 */
export const getDefaultHeaders = (): CommonHeaders => {
  return {
    'Content-Type': 'application/json',
  };
};

/**
 * 获取客户端请求头
 * 优先从 URL 参数获取，然后从 localStorage 获取，最后使用默认值
 */
export const getClientHeaders = (): CommonHeaders => {
  // console.log('getClientHeaders')
  // 默认头信息
  const defaultHeaders = getDefaultHeaders();
  
  // 从 localStorage 获取存储的头信息
  let storedHeaders: Partial<CommonHeaders> = {};
  if (typeof window !== 'undefined') {
    try {
      const storedData = localStorage.getItem('common_headers');
      if (storedData) {
        // console.log('storedData', storedData)
        storedHeaders = JSON.parse(storedData);
      }
    } catch (error) {
      console.error('Failed to parse headers from localStorage:', error);
    }
  }
  
  // 从 URL 参数获取最新头信息
  const urlHeaders: Partial<CommonHeaders> = {};
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const headerKeys = ['clientType', 'deviceId', 'lang', 'versionCode'];
    // console.log('params', params)
    headerKeys.forEach(key => {
      const value = params.get(key);
      if (value) urlHeaders[key] = value;
    });
    
    // 如果 URL 中有新的头信息，更新到 localStorage
    if (Object.keys(urlHeaders).length > 0) {
      const updatedHeaders = { ...storedHeaders, ...urlHeaders };
      try {
        localStorage.setItem('common_headers', JSON.stringify(updatedHeaders));
      } catch (error) {
        console.error('Failed to save headers to localStorage:', error);
      }
    }
  }
  
  // 合并所有头信息，优先级: URL > localStorage > 默认值
  return {
    ...defaultHeaders,
    ...storedHeaders,
    ...urlHeaders
  };
};

/**
 * 保存服务端请求头
 * 仅在服务端环境中有效
 */
export const saveServerHeaders = (headers: Record<string, string | string[] | undefined>): void => {
  if (typeof window === 'undefined') {
    const headerKeys = ['clientType', 'deviceId', 'lang', 'versionCode'];
    
    headerKeys.forEach(key => {
      const value = headers[key];
      // console.log('key -- value', key, value);
      
      // 处理数组情况 - 取第一个值
      if (Array.isArray(value) && value.length > 0) {
        globalServerHeaders[key] = value[0];
      } 
      // 处理字符串情况
      else if (value && typeof value === 'string') {
        globalServerHeaders[key] = value;
      }
    });
  }
};

/**
 * 获取服务端请求头
 * 根据环境返回适当的请求头
 */
export const getServerHeaders = (): CommonHeaders => {
  if (typeof window !== 'undefined') {
    // 客户端：从 URL 参数获取
    return getClientHeaders();
  } else {
    // 服务端：从全局变量获取
    return { ...globalServerHeaders };
  }
};

/**
 * 解析服务端请求查询参数
 * 提取请求头信息并保存到全局变量
 */
export const parseServerHeaders = (query: Record<string, string | string[] | undefined>): CommonHeaders => {
  const headers = getDefaultHeaders();
  const headerKeys = ['clientType', 'deviceId', 'lang', 'versionCode'];
  
  headerKeys.forEach(key => {
    const value = query[key];
    
    // 处理数组情况 - 取第一个值
    if (Array.isArray(value) && value.length > 0) {
      headers[key] = value[0];
    }
    // 处理字符串情况
    else if (value && typeof value === 'string') {
      headers[key] = value;
    }
  });

  // 保存到全局变量
  if (typeof window === 'undefined') {
    saveServerHeaders(query);
  }

  return headers;
};

/**
 * 获取通用请求头
 * 这是给 useRequest 使用的主要函数
 * 根据环境自动选择合适的请求头获取方式
 */
export const getCommonHeaders = (): CommonHeaders => {
  return typeof window === 'undefined' 
    ? getServerHeaders() 
    : getClientHeaders();
};

/**
 * 初始化客户端请求头
 * 用于在应用启动时初始化请求头
 * 兼容 _app.tsx 中的调用
 */
export const initClientHeaders = (): void => {
  // console.log('initClientHeaders');
  if (typeof window !== 'undefined') {
    // 客户端环境：从 URL 和 localStorage 获取请求头
    getClientHeaders();
  }
};

/**
 * 设置服务端查询参数
 * 用于在服务端渲染时设置请求头
 * 兼容 _app.tsx 中的调用
 */
export const setServerQuery = (query: Record<string, string | string[] | undefined>): void => {
  // console.log('setServerQuery', query);
  if (typeof window === 'undefined') {
    // 服务端环境：保存请求头
    saveServerHeaders(query);
  }
}; 